import React, { useState, useEffect } from "react";
import { Box, useTheme, useMediaQuery } from "@mui/material";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useDispatch } from "react-redux";
import { logoutUserAction } from "../../store/slices/authSlice";
import ProfileSidebar from "./ProfileSidebar";
import MyProfile from "./MyProfile";
import DonateOnce from "./DonateOnce";
import DonateMonthly from "./DonateMonthly";
import OverseasDonation from "./OverseasDonation";
import MyTransactions from "./MyTransactions";
import Subscriptions from "./Subscriptions";
import Form80G from "./Form80G";
import Form10B from "./Form10B";
import ProfileUrgentCampaignSlider from "../../components/common/MyProfile/ProfileUrgentCampaignSlider";

const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery("(min-width:600px) and (max-width:1280px)");
  const isBelow1200 = useMediaQuery("(max-width: 1200px)");
  const [activeSection, setActiveSection] = useState("My Profile");

  // Update active section based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/profile" || path === "/profile/my-profile") {
      setActiveSection("My Profile");
    } else if (path === "/profile/donate-once") {
      setActiveSection("Donate Once");
    } else if (path === "/profile/donate-monthly") {
      setActiveSection("Donate Monthly");
    } else if (path === "/profile/overseas-donation") {
      setActiveSection("Overseas Donation");
    } else if (path === "/profile/my-transactions") {
      setActiveSection("My Transactions");
    } else if (path === "/profile/subscriptions") {
      setActiveSection("My Subscriptions");
    } else if (path === "/profile/80g-forms" || path === "/profile/80g-form") {
      setActiveSection("Download 80G Form");
    } else if (path === "/profile/10b-forms" || path === "/profile/10b-form") {
      setActiveSection("Download 10B Form");
    }
  }, [location.pathname]);

  const handleSidebarClick = (itemId) => {
    setActiveSection(itemId);

    // Navigate to specific routes
    if (itemId === "My Profile") {
      navigate("/profile/my-profile");
    } else if (itemId === "Donate Once") {
      navigate("/profile/donate-once");
    } else if (itemId === "Donate Monthly") {
      navigate("/profile/donate-monthly");
    } else if (itemId === "Overseas Donation") {
      navigate("/profile/overseas-donation");
    } else if (itemId === "My Transactions") {
      navigate("/profile/my-transactions");
    } else if (itemId === "My Subscriptions") {
      navigate("/profile/subscriptions");
    } else if (itemId === "Download 80G Form") {
      // Navigate to table view of 80G forms
      navigate("/profile/80g-forms");
    } else if (itemId === "Download 10B Form") {
      // Navigate to table view of 10B forms
      navigate("/profile/10b-forms");
    } else if (itemId === "Logout") {
      // Handle logout logic
      handleLogout();
    }
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutUserAction()).unwrap();
      // Navigate to home page after successful logout
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      // Even if logout fails on server, clear local state
      localStorage.removeItem("authToken");
      navigate("/");
    }
  };

  // For mobile, we still need to render the appropriate content based on the route
  if (isMobile) {
    return (
      <>
        <Box
          sx={{
            width: "100%",
            padding: "0px 15px 15px 15px" ,
            boxSizing: "border-box",
          }}
        >
          {location.pathname === "/profile" ? (
            // Only show the sidebar on the main profile page
            <ProfileSidebar
              onItemClick={handleSidebarClick}
              activeItem={activeSection}
            />
          ) : (
            // On other profile routes, show the content with the Outlet
            <Outlet />
          )}
        </Box>

        {/* Show ProfileUrgentCampaignSlider for all routes except my-profile and outside the container for full width */}
        {location.pathname !== "/profile" &&
          location.pathname !== "/profile/my-profile" && (
            <Box
              sx={{
                width: "100%",
                mt: isMobile ? "0" : "30px",
                px: 0, // No horizontal padding to make it full width
              }}
            >
              <ProfileUrgentCampaignSlider />
            </Box>
          )}
      </>
    );
  }

  return (
    <>
      <Box
        sx={{
          width: "94.44%",
          margin: "0 auto",
          maxWidth: "1440px",
          display: "flex",
          flexDirection: isTablet ? "column" : "row",
          gap: isTablet ? "20px" : "30px",
        }}
      >
        {/* Left Section - Profile Sidebar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: isTablet ? "flex-start" : "center",
            marginBottom: isTablet ? "10px" : 0,
          }}
        >
          <ProfileSidebar
            onItemClick={handleSidebarClick}
            activeItem={activeSection}
          />
        </Box>

        {/* Right Section - Dynamic Content */}
        <Box
          sx={{
            width: "100%",
            minHeight: "400px",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Urgent slider: not on profile hub (/profile) or my-profile — only on other profile subpages */}
      {location.pathname !== "/profile/my-profile" &&
        location.pathname !== "/profile" && (
        <Box sx={{ width: "94.44%", margin: "0 auto", maxWidth: "1440px" }}>
          <ProfileUrgentCampaignSlider />
        </Box>
      )}
    </>
  );
};

export default Profile;
